import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/referees/';
const TEAMS_API_URL = '/teams/';

const RefereeForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    name: '',
    long_name: '',
    country: '',
  });

  useEffect(() => {
    axios.get(TEAMS_API_URL).then(res => setTeams(res.data)).catch(() => setTeams([]));
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          name: res.data.name || '',
          long_name: res.data.long_name || '',
          country: res.data.country ? res.data.country.id : '',
        });
      }).catch(() => setError('Failed to load referee')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, country: form.country || null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Referee updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Referee created successfully');
        setForm({ name: '', long_name: '', country: '' });
      }
      setTimeout(() => navigate('/football/referees'), 1000);
    } catch (err: any) {
      setError('Failed to save referee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Referee</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="Long Name" name="long_name" value={form.long_name} onChange={handleChange} fullWidth />
          <TextField select label="Country (Team)" name="country" value={form.country} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {teams.map(team => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}
          </TextField>
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default RefereeForm; 